<template>
    <div>
        <b-row class="align-items-end mb-3 filter-row">
            <b-col cols="auto">
                <b-form-group label="From" class="mb-0">
                    <b-form-input v-model="fromDate" type="date" :max="toDate" />
                </b-form-group>
            </b-col>
            <b-col cols="auto">
                <b-form-group label="To" class="mb-0">
                    <b-form-input v-model="toDate" type="date" :min="fromDate" />
                </b-form-group>
            </b-col>
            <b-col cols="auto">
                <b-button variant="primary" class="m-0" @click="applyFilter" :disabled="!canApply">
                    <i class="fa fa-search"></i> Apply
                </b-button>
            </b-col>
        </b-row>

        <b-table ref="logTable" :items="logs" :fields="fields" :busy="loading" show-empty
            :per-page="pageSize" :current-page="pageNumber"
            empty-text="No access logs found for the selected date range" small responsive>
            <template #table-busy>
                <div class="text-center my-3">
                    <b-spinner small class="align-middle mr-2"></b-spinner>
                    <span>Loading access logs…</span>
                </div>
            </template>

            <template #cell(loginAt)="{ value }">
                {{ formatDateTime(value) }}
            </template>

            <template #cell(sessionDurationMinutes)="{ value }">
                {{ value == null ? '—' : value }}
            </template>

            <template #cell(pages)="{ item, toggleDetails, detailsShowing }">
                <b-button size="sm" variant="primary" @click="onTogglePages(item, toggleDetails, detailsShowing)">
                    <i class="fa" :class="detailsShowing ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                    {{ item.pageCount }} page{{ item.pageCount === 1 ? '' : 's' }}
                </b-button>
            </template>

            <template #row-details="{ item }">
                <b-card class="page-trail-card">
                    <div v-if="loadingPagesFor === item.id" class="text-muted">
                        <b-spinner small class="align-middle mr-2"></b-spinner> Loading page trail…
                    </div>
                    <div v-else-if="!(pagesByLog[item.id] && pagesByLog[item.id].length)" class="text-muted">
                        No pages recorded for this session.
                    </div>
                    <ol v-else class="page-trail mb-0">
                        <li v-for="page in pagesByLog[item.id]" :key="page.id">
                            <span class="page-time">{{ formatDateTime(page.visitedAt) }}</span>
                            <span class="page-path">{{ page.title || page.path }}</span>
                            <small v-if="page.title" class="text-muted">{{ page.path }}</small>
                        </li>
                    </ol>
                </b-card>
            </template>
        </b-table>

        <BottomPagerBar v-model="pageNumber" item-name="session" show-total show-page-size-selector
            :total-rows="logs.length" :per-page="pageSize" :loading="loading"
            @change-page-size="onPageSizeChange($event)">
            <template #left>
                <b-button size="sm" class="m-0" variant="primary" @click="fetchLogs">
                    <i class="fa fa-sync"></i> Refresh
                </b-button>
            </template>
        </BottomPagerBar>
    </div>
</template>

<script lang="ts">
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import { BTable } from 'bootstrap-vue';
import { Component, Prop, Ref, Vue } from 'vue-property-decorator';
import axios from 'axios';
import moment from 'moment';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { UserAccessLog, UserAccessLogPage } from '@/models/UserAccessLog';

/**
 * Reusable portal access-log report. Renders the shared UserAccessLog sessions for a portal with
 * date-range filtering and an expandable per-session page trail. Designed to be embedded in any
 * portal management page (Manufacturer, FRM, Provider Web) by pointing it at that portal's
 * access-log endpoints via the endpointBase prop.
 */
@Component({
    components: {
        BottomPagerBar
    }
})
export default class UserAccessLogReport extends Vue {
    /** Base path of the portal's access-log endpoints, e.g. "/api/ManufacturerPortal/access-logs". */
    @Prop({ type: String, required: true }) endpointBase!: string;
    /** Whether to show the Client column (manufacturer/company). */
    @Prop({ type: Boolean, default: true }) showClient!: boolean;
    /** Whether to show the Role column. */
    @Prop({ type: Boolean, default: true }) showRole!: boolean;
    /** Whether to show the Zone column. */
    @Prop({ type: Boolean, default: true }) showZone!: boolean;

    @Ref() logTable!: BTable;

    logs: UserAccessLog[] = [];
    pagesByLog: Record<number, UserAccessLogPage[]> = {};
    loadingPagesFor: number | null = null;
    loading = false;
    pageNumber = 1;
    pageSize = 20;
    fromDate = '';
    toDate = '';

    get fields() {
        return [
            { key: 'displayName', label: 'User', sortable: true, formatter: (_v: string, _k: string, item: UserAccessLog) => item.displayName || item.email || item.username },
            ...(this.showClient ? [{ key: 'client', label: 'Client', sortable: true }] : []),
            ...(this.showRole ? [{ key: 'role', label: 'Role', sortable: true }] : []),
            ...(this.showZone ? [{ key: 'zone', label: 'Zone', sortable: true }] : []),
            { key: 'loginAt', label: 'Login', sortable: true },
            { key: 'sessionDurationMinutes', label: 'Duration (min)', sortable: true },
            { key: 'deviceBrowser', label: 'Device / Browser' },
            { key: 'timeZone', label: 'Time Zone' },
            { key: 'pages', label: 'Pages' }
        ];
    }

    mounted() {
        // Default to the user's local last-30-days. Build the yyyy-MM-dd from local time (not
        // toISOString, which is UTC and would show a future "To" date for users west of UTC).
        this.toDate = moment().format('YYYY-MM-DD');
        this.fromDate = moment().subtract(30, 'days').format('YYYY-MM-DD');
        this.fetchLogs();
    }

    // Only allow Apply when both bounds are present and valid, so a cleared date field can't submit a
    // blank/invalid range. fetchLogs itself also tolerates missing bounds (Refresh falls back to the
    // server default), but disabling Apply gives the user immediate feedback that the range is incomplete.
    get canApply(): boolean {
        return !this.loading
            && moment(this.fromDate, 'YYYY-MM-DD', true).isValid()
            && moment(this.toDate, 'YYYY-MM-DD', true).isValid();
    }

    applyFilter() {
        this.pageNumber = 1;
        this.fetchLogs();
    }

    onPageSizeChange(newSize: number) {
        this.pageSize = newSize;
        // Reset to the first page so a larger size can't strand the user past the new last page.
        this.pageNumber = 1;
    }

    async fetchLogs() {
        try {
            this.loading = true;
            this.pagesByLog = {};
            // Send the selected local day-range as UTC instants so the server's UTC LoginAt filter
            // lines up with the local times shown in the table (see formatDateTime). Strict-parse the
            // yyyy-MM-dd inputs and omit a bound that is blank or invalid (e.g. a cleared date field) so
            // the server falls back to its own default instead of receiving a null/invalid instant.
            const from = moment(this.fromDate, 'YYYY-MM-DD', true);
            const to = moment(this.toDate, 'YYYY-MM-DD', true);
            const params: Record<string, string> = {};
            if (from.isValid()) params.fromDate = from.startOf('day').toISOString();
            if (to.isValid()) params.toDate = to.endOf('day').toISOString();
            const resp = await axios.get<UserAccessLog[]>(this.endpointBase, { params });
            this.logs = resp.data;
        } catch (err: any) {
            console.error(err);
            this.$notification(NotificationOptions.error(err.response?.data || 'Failed to load access logs'));
        } finally {
            this.loading = false;
        }
    }

    async onTogglePages(item: UserAccessLog, toggleDetails: () => void, detailsShowing: boolean) {
        toggleDetails();
        // Lazy-load the page trail the first time the row is expanded.
        if (!detailsShowing && !this.pagesByLog[item.id]) {
            await this.fetchPages(item.id);
        }
    }

    async fetchPages(id: number) {
        try {
            this.loadingPagesFor = id;
            const resp = await axios.get<UserAccessLogPage[]>(`${this.endpointBase}/${id}/pages`);
            this.$set(this.pagesByLog, id, resp.data);
        } catch (err: any) {
            console.error(err);
            this.$set(this.pagesByLog, id, []);
            this.$notification(NotificationOptions.error(err.response?.data || 'Failed to load page trail'));
        } finally {
            this.loadingPagesFor = null;
        }
    }

    formatDateTime(value?: string | null): string {
        // Server timestamps are UTC; moment.utc parses both naive and offset-bearing values, shown local.
        return value ? moment.utc(value).local().format('L LT') : '';
    }
}
</script>

<style scoped>
.filter-row > [class^='col'] {
    padding-bottom: 0.25rem;
}

.page-trail-card {
    background-color: #f8f9fa;
}

.page-trail li {
    margin-bottom: 0.25rem;
}

.page-trail .page-time {
    display: inline-block;
    min-width: 12rem;
    color: #6c757d;
}

.page-trail .page-path {
    font-weight: 500;
    margin-right: 0.5rem;
}
</style>
